import { gql, useApolloClient, useMutation } from '@apollo/client';
import type { AsCreationAttributes, Show } from '@shady-pines/db';
import { head, isNil } from 'lodash';
import { useCallback } from 'react';
import type { MutationResponse } from '../types';
import { useSupabase } from '../use-supabase';

const CREATE_SHOW = gql`
  mutation CreateShow(
    $name: String!
    $description: String!
    $user_id: UUID!
    $active: Boolean!
    $day_of_week: String
    $start_time: Int
    $end_time: Int
    $default_push_title: String
    $default_push_message: String
    $details_url: String
    $dj_name_override: String
  ) {
    insertIntoshowsCollection(
      objects: [
        {
          name: $name
          description: $description
          user_id: $user_id
          active: $active
          day_of_week: $day_of_week
          start_time: $start_time
          end_time: $end_time
          default_push_title: $default_push_title
          default_push_message: $default_push_message
          details_url: $details_url
          dj_name_override: $dj_name_override
        }
      ]
    ) {
      records {
        __typename
        id
        name
        description
        user_id
        active
        day_of_week
        start_time
        end_time
        default_push_title
        default_push_message
        details_url
        dj_name_override
      }
    }
  }
`;

export const useCreateShow = () => {
  const supabase = useSupabase();
  const client = useApolloClient();
  const [createShowMutation, { loading, error }] =
    useMutation<MutationResponse<'insertIntoshowsCollection', Show>>(CREATE_SHOW);

  const create = useCallback(
    async (input: AsCreationAttributes<Show>) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error('User must be authenticated to create shows');
      }

      const result = await createShowMutation({
        variables: input,
      });

      const createdShow = head(result.data?.insertIntoshowsCollection.records);
      if (isNil(createdShow)) {
        throw new Error('Failed to create show');
      }

      await client.refetchQueries({
        include: ['GetShows'],
      });

      return createdShow;
    },
    [createShowMutation, supabase, client],
  );

  return {
    createShow: create,
    loading,
    error,
  };
};
